import React from 'react';
import { PartialStarIcon } from '../SvgIcons/PartialStar';
import { StarIcon } from '../SvgIcons/Star';
import { StarOutlineIcon } from '../SvgIcons/StarOutline';

import Styled from './StarRatings.style';

import P from '../../typography/Paragraphs';

export type StarRatingsProps = {
  /** How many stars to render in total. For Reviews on a 1 to 5 scale, 5 would be a good choice */
  totalStars: number;
  /** How many filled in stars to render in total. */
  activeStars: number;
  /** Number of total reviews */
  reviewCount: string;
  /** Determines color of stars & stroke */
  fillColor?: string;
  /** @deprecated Use starSize instead */
  size?: 'small' | 'default';
  /** How big should the star be. Small: 14px, default: 16px */
  starSize?: 'small' | 'default';
  /** How big should the text be. Small: 14px, default: 16px */
  textSize?: 'small' | 'default';
};

/**
 * Renders a star-based rating component, providing: the overall rating and the number of reviews considered for the overall rating
 *
 * ## Import
 *```typescript
 * import StarRatings from '@zola/zola-ui/src/components/StarRatings/StarRatings';
 *```
 *
 * ## Example
 * Shows a 4.8 rating out of 5 with 37 reviews
 *
 * ```typescript
 * <StarRatings totalStars={5} activeStars={4.8} reviewCount="37 reviews" />
 * ```
 */

const StarRatings = (props: StarRatingsProps): JSX.Element => {
  const {
    activeStars,
    fillColor,
    reviewCount,
    starSize = props.size || 'default',
    textSize = props.size || 'default',
    totalStars = 5,
  } = props;
  const filledInStars = Math.floor(activeStars);
  const remainingDecimals = activeStars - Math.floor(activeStars);
  const height = starSize === 'small' ? 14 : 16;
  const width = starSize === 'small' ? 14 : 16;
  const iconProps = { height, width };
  const outlineIconProps = { height: height - 2, width: width - 2 }; // The star outline is, annoyingly, larger than the filled in star
  const Text = textSize === 'small' ? P.BodySmall : P.BodyBase;

  return (
    <Styled.StarRatings>
      <Styled.RatingsWrapper>
        <Styled.Stars>
          {[...new Array(totalStars)].map((arr, index) => {
            if (remainingDecimals > 0 && index === filledInStars) {
              return <PartialStarIcon color={fillColor} key={index} {...iconProps} />;
            }
            return index < activeStars ? (
              <StarIcon color={fillColor} key={index} {...iconProps} />
            ) : (
              <StarOutlineIcon color={fillColor} key={index} {...outlineIconProps} />
            );
          })}
        </Styled.Stars>
        {reviewCount && (
          <Text>
            {activeStars} <span>({reviewCount})</span>
          </Text>
        )}
      </Styled.RatingsWrapper>
    </Styled.StarRatings>
  );
};

export default StarRatings;
